import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, MessageCircle, AlertCircle } from "lucide-react";
import { getPaymentStatus, waLink } from "@/lib/api";
import { LogoImg } from "@/components/landing/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "loading", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null });
      return;
    }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          clearInterval(timer);
        } else if (attempts >= 30) {
          setState({ phase: "timeout", data });
          clearInterval(timer);
        }
      } catch {
        if (attempts >= 30) {
          setState({ phase: "error", data: null });
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  const { phase, data } = state;

  return (
    <div className="min-h-screen bg-alabaster flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg rounded-[2rem] bg-white border border-line p-10 text-center shadow-[0_30px_60px_rgba(15,23,20,0.12)]"
        data-testid="payment-success-page"
      >
        <div className="flex justify-center mb-6">
          <LogoImg className="h-14 w-auto" />
        </div>

        {phase === "loading" && (
          <div data-testid="payment-loading">
            <Loader2 size={40} className="mx-auto animate-spin text-moss" />
            <h1 className="mt-5 font-serif text-2xl font-semibold text-moss">
              Conferma del pagamento in corso...
            </h1>
            <p className="mt-2 text-sm text-sub">Qualche secondo e il tuo voucher è attivo.</p>
          </div>
        )}

        {phase === "paid" && data && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={48} className="mx-auto text-mosslight" />
            <h1 className="mt-4 font-serif text-3xl font-semibold text-moss">
              Pagamento riuscito{data.customer_name ? `, ${data.customer_name.split(" ")[0]}` : ""}!
            </h1>
            <p className="mt-2 text-sm text-sub">
              {data.package_name}
            </p>
            {data.tier && (
              <span className="mt-3 inline-block rounded-full bg-sage px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-sagetext" data-testid="voucher-tier-badge">
                {data.tier}
              </span>
            )}
            <div className="mt-5 rounded-2xl bg-tint border-2 border-dashed border-gold/60 px-6 py-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sub font-bold">
                Il tuo Voucher
              </p>
              <p data-testid="credit-code-display" className="mt-1.5 font-serif text-3xl font-bold tracking-wide text-terracotta">
                {data.credit_code}
              </p>
            </div>
            <p className="mt-4 text-xs text-sub">
              Salva questo codice, ti identifica quando mi contatti.{" "}
              {data.available_now
                ? "Il tuo voucher è attivo adesso."
                : `Il voucher si attiva ${data.schedule_label}, ma puoi già scrivermi su WhatsApp per organizzarci.`}
              {" "}Ora mandami una foto del problema su WhatsApp e fissiamo la chiamata.
            </p>
            {!data.available_now && data.tier !== "Voucher VIP" && (
              <p className="mt-3 text-xs font-semibold" data-testid="success-upgrade-hint">
                <a href="/#pacchetti" className="text-terracotta hover:underline">
                  Ti serve subito? Con il Voucher VIP saresti coperto adesso, 7 giorni su 7 →
                </a>
              </p>
            )}
            <a
              href={waLink(`${(data.tier || "Voucher").toUpperCase()} — Codice ${data.credit_code}. Buongiorno Dott. Vincenti, ho appena acquistato la consulenza e le invio le foto del problema.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-direct-link"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-moss py-4 text-base font-semibold text-alabaster transition-all duration-200 hover:bg-mosslight hover:-translate-y-0.5"
            >
              <MessageCircle size={18} />
              Avvia chat WhatsApp col Dottore
            </a>
            <Link
              to="/"
              data-testid="back-home-link"
              className="mt-4 inline-block text-sm font-medium text-sub hover:text-moss transition-colors"
            >
              ← Torna alla home
            </Link>
          </div>
        )}

        {(phase === "timeout" || phase === "error") && (
          <div data-testid="payment-pending-message">
            <AlertCircle size={44} className="mx-auto text-gold" />
            <h1 className="mt-4 font-serif text-2xl font-semibold text-moss">
              Stiamo ancora verificando il pagamento
            </h1>
            <p className="mt-2 text-sm text-sub">
              Se hai completato il pagamento, il voucher si attiverà a breve. Per qualsiasi
              dubbio{" "}
              <a href={waLink("Buongiorno, ho appena effettuato un pagamento e attendo la conferma.")}
                target="_blank" rel="noopener noreferrer" className="font-semibold text-terracotta underline">
                contattami qui
              </a>
              .
            </p>
            <Link to="/" className="mt-5 inline-block text-sm font-medium text-sub hover:text-moss">
              ← Torna alla home
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
